import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Search, Filter, Bell, AlertTriangle, CheckCircle, Clock, Eye, X,
  MoreHorizontal, Loader2, Plus,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  useNotifications,
  useAcknowledgeNotification,
  useResolveNotification,
  useDismissNotification,
} from '../hooks/api/notifications';
import { usePermission } from '../hooks/useMe';
import { CreateAlertDialog } from './CreateAlertDialog';
import { toast } from 'sonner';

const SEVERITY = {
  critical: { color: 'bg-red-500',    icon: AlertTriangle, textColor: 'text-red-500' },
  warning:  { color: 'bg-yellow-500', icon: AlertTriangle, textColor: 'text-yellow-500' },
  info:     { color: 'bg-blue-500',   icon: Bell,          textColor: 'text-blue-500' },
};

function statusFromNotif(n) {
  return n.read_at ? 'resolved' : 'active';
}

const STATUS = {
  active:   { color: 'bg-red-500',   label: 'Active' },
  resolved: { color: 'bg-green-500', label: 'Resolved' },
};

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toISOString().slice(0, 16).replace('T', ' ');
  } catch { return s; }
}

export function Alerts() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const canCreate = usePermission('notification:create');

  const { data, isLoading, isError, error } = useNotifications({
    status: statusFilter === 'all' ? undefined
            : statusFilter === 'active' ? 'unread'
            : statusFilter === 'resolved' ? 'read'
            : undefined,
    severity: severityFilter === 'all' ? undefined : severityFilter,
    q: searchTerm || undefined,
    page_size: 200,
  });

  const ack = useAcknowledgeNotification();
  const resolve = useResolveNotification();
  const dismiss = useDismissNotification();

  const items = data?.data || [];
  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((n) => !n.read_at).length;
    const critical = items.filter((n) => n.type === 'critical').length;
    const today = new Date().toDateString();
    const resolvedToday = items.filter(
      (n) => n.read_at && new Date(n.read_at).toDateString() === today,
    ).length;
    return { total, active, critical, resolvedToday };
  }, [items]);

  return (
    <div className="space-y-6">
      {/* Page header — Create Alert is gated on `notification:create`, so
          client_admins (and super-admins) see it; client_users + viewers
          don't. */}
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Bell className="w-6 h-6" /> Alerts
          </h1>
          <p className="text-sm text-muted-foreground">
            Track issues, broadcast notices, and dispatch alerts to client members.
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Create Alert
          </Button>
        )}
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard label="Total Alerts"     value={stats.total}        bg="bg-blue-500"   icon={Bell} />
        <StatCard label="Active Alerts"    value={stats.active}       bg="bg-red-500"    icon={AlertTriangle} valueClass="text-red-500" />
        <StatCard label="Critical"         value={stats.critical}     bg="bg-orange-500" icon={AlertTriangle} valueClass="text-orange-500" />
        <StatCard label="Resolved Today"   value={stats.resolvedToday} bg="bg-green-500"  icon={CheckCircle}   valueClass="text-green-500" />
      </div>

      {/* Filters */}
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search alerts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48"><SelectValue placeholder="Filter by status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full sm:w-48"><SelectValue placeholder="Filter by severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Information</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Alerts ({items.length})</span>
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Advanced Filters
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading alerts…
            </div>
          )}
          {isError && (
            <div className="py-6 px-6 text-sm text-destructive">
              Failed to load: {error?.message}
            </div>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No alerts match your filters.
            </div>
          )}
          {!isLoading && items.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-muted/50">
                    <TableHead className="font-semibold">Alert ID</TableHead>
                    <TableHead className="font-semibold">Alert Details</TableHead>
                    <TableHead className="font-semibold">Date &amp; Time</TableHead>
                    <TableHead className="font-semibold">Source</TableHead>
                    <TableHead className="font-semibold">Recipient</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Severity</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((n) => {
                    const sev = SEVERITY[n.type] || SEVERITY.info;
                    const status = statusFromNotif(n);
                    const stat = STATUS[status];
                    const SeverityIcon = sev.icon;
                    const source = n.payload?.source || '—';
                    const recipient = n.payload?.recipient || '—';
                    return (
                      <TableRow key={n.id} className="border-border hover:bg-muted/50">
                        <TableCell className="font-mono text-xs">{n.id.slice(0, 8)}</TableCell>
                        <TableCell>
                          <div className="space-y-1 max-w-sm">
                            <div className="font-medium text-foreground">{n.title}</div>
                            {n.body && (
                              <div className="text-sm text-muted-foreground line-clamp-2">{n.body}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Clock className="w-3 h-3 mr-1 text-muted-foreground" />
                            {fmtDate(n.created_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{source}</TableCell>
                        <TableCell className="text-sm">{recipient}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${stat.color}`}></div>
                            <span className="text-sm font-medium">{stat.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <SeverityIcon className={`w-4 h-4 ${sev.textColor}`} />
                            <span className={`text-sm font-medium capitalize ${sev.textColor}`}>
                              {n.type}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {status === 'active' && (
                                <DropdownMenuItem onClick={() => ack.mutate(n.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Acknowledge
                                </DropdownMenuItem>
                              )}
                              {status !== 'resolved' && (
                                <DropdownMenuItem onClick={() => resolve.mutate(n.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Mark as Resolved
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => dismiss.mutate(n.id)}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Dismiss Alert
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create-alert dialog (rendered conditionally so it's a no-op for
          users without the create permission). */}
      {canCreate && (
        <CreateAlertDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(n) => {
            toast.success(
              n.user_id
                ? `Alert "${n.title}" sent.`
                : `Alert "${n.title}" broadcast to all members.`,
            );
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, bg, icon: Icon, valueClass }) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <h3 className={`text-2xl font-bold ${valueClass || 'text-foreground'}`}>{value}</h3>
          </div>
          <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
